import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Spin,
  Steps,
  Tooltip,
  Typography,
} from 'antd';
import { postVerificarImportesConvenio } from 'api/recaudacion';
import {
  generarParcialidades,
  postCargosConvenidos,
  postConvenio,
  postUploadFile,
} from 'api/recaudacion/convenios';
import { getTiposDeConvenios } from 'api/recaudacion/tiposDeConvenio';
import { getPersonalDeConvenios } from 'api/catalogos/personalDeConvenios';
import { postObtenerReporte } from 'api/reporteador';
import NumericInput from 'components/NumericInput';
import Previewer from 'components/Previewer';
import Select from 'components/Select';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SET_PADRON_REQUIERE_FETCH } from 'store/reducers/caja';
import styled from 'styled-components';
import { DATE_FORMAT } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { capitalize } from 'utils/normalizers';
import { getContenTypePorTipoDePadron } from 'utils/padrones';
import padronToString from 'utils/padronesToString';
import round from 'utils/round';
import currencyFormatter from 'utils/toCurrency';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import SectionCobros from './SectionCobroParcialidades';

const { Text, Title } = Typography;

const stepsEnum = {
  CONFIRMACION: 0,
  PARCIALIDADES: 1,
  COBRO: 2,
};

const tiposDeVencimientoEnum = {
  FINALES: 1,
  FECHA_DE_CREACION: 2,
  INICIALES: 3,
  CUSTOM: 4,
};

const isDev = process.env.NODE_ENV === 'development';

const groupCargos = (_cargos = []) => {
  const cargosPadres = _cargos
    .filter((c) => !c.es_accesorio && c.tipo_de_cargo.es_convenio);
  const accesorios = cargosPadres.map((cp) => _cargos
    .filter((_c) => _c.es_accesorio && _c.cargo_padre === cp.id)).flat();

  const conveniables = cargosPadres.concat(accesorios.filter((a) => cargosPadres.map((c) => c.id)
    .includes(a.cargo_padre)));

  const cargosPadresNC = _cargos
    .filter((c) => !c.es_accesorio && !c.tipo_de_cargo.es_convenio);
  const accesoriosNC = cargosPadresNC.map((cp) => _cargos
    .filter((_c) => _c.es_accesorio && _c.cargo_padre === cp.id)).flat();
  const noConveniables = cargosPadresNC.concat(accesoriosNC
    .filter((a) => cargosPadresNC.map((c) => c.id)
      .includes(a.cargo_padre)));
  return { conveniables, noConveniables };
};

const outputDiscountsStructure = {
  descuento_base_id: [],
  importe_del_descuento_base: 0,
  descuento_de_recargo_id: [],
  importe_del_descuento_del_recargo: 0,
  descuento_de_actualizacion_id: [],
  importe_del_descuento_de_actualizacion: 0,
  descuento_de_gasto_de_ejecucion_id: [],
  importe_del_descuento_de_gasto_de_ejecucion: 0,
  // Recargos multas
  descuento_de_recargo_multa_id: [],
  importe_del_descuento_del_recargo_multa: 0,
  // Gastos multas
  descuento_de_gasto_multa_id: [],
  importe_del_descuento_de_gasto_multa: 0,
};

const ModalConvenio = ({
  visible,
  setVisible,
  tipoDePadron,
  padron,
  hasPermission,
  cargos,
  resetCaja,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const selectedRowKeys = useSelector(({ caja }) => caja?.selectedRowKeys?.[padron?.key]) || [];
  const { ciudadano } = useSelector(({ caja }) => caja);
  const [form] = Form.useForm();
  const user = useSelector(({ auth }) => auth.user);
  const [loading, setLoading] = useState(false);
  const [tipoDeConvenios, setTipoDeConvenios] = useState([]);
  const [personalDeConvenios, setPersonalDeConvenios] = useState([]);
  const [groupedCargos, setGroupedCargos] = useState({ conveniables: [], noConveniables: [] });
  const [currentStep, setCurrentStep] = useState(stepsEnum.CONFIRMACION);
  const [tipoSelected, setTipoSelected] = useState(null);
  const [hasResponsable, setHasResponsable] = useState(false);
  const [hasAfianzadora, setHasAfianzadora] = useState(false);
  const [base64, setBase64] = useState('');
  const [visiblePreview, setVisiblePreview] = useState(false);
  const ciudadanoName = useRef('');
  const contribuyenteName = useRef('');
  const convenio = useRef(null);
  const personalAutorizado = useRef(null);

  const processCargoDiscounts = (cargo) => {
    const {
      descuentos_aplicables = [],
      descuentos_especiales = [],
      recargos = [],
      recargosMulta = [],
      gastos = [],
      gastosMulta = [],
      actualizaciones = [],
    } = cargo;

    const descuento_base = [
      ...descuentos_aplicables,
      ...descuentos_especiales,
    ].filter((d) => d.aplicado);

    const descuento_de_actualizacion = [
      ...actualizaciones,
    ].map((a) => a.descuentos.filter((d) => d.aplicado)).flat();

    const descuento_de_gasto_de_ejecucion = [
      ...gastos,
    ].map((g) => g.descuentos.filter((d) => d.aplicado)).flat();

    const descuento_de_recargo = [
      ...recargos,
    ].map((r) => r.descuentos.filter((d) => d.aplicado)).flat();

    const descuento_de_recargo_multa = [
      ...recargosMulta,
    ].map((r) => r.descuentos.filter((d) => d.aplicado)).flat();

    const descuento_de_gasto_multa = [
      ...gastosMulta,
    ].map((g) => g.descuentos.filter((d) => d.aplicado)).flat();

    return {
      descuento_base_id: descuento_base.map((d) => d.id),
      importe_del_descuento_base: descuento_base
        .reduce((acc, curr) => acc + (curr?.importe_total || 0), 0),
      descuento_de_recargo_id: descuento_de_recargo.map((d) => d.id),
      importe_del_descuento_del_recargo: descuento_de_recargo
        .reduce((acc, curr) => acc + (curr?.importe_total || 0), 0),
      descuento_de_actualizacion_id: descuento_de_actualizacion.map((d) => d.id),
      importe_del_descuento_de_actualizacion: descuento_de_actualizacion
        .reduce((acc, curr) => acc + (curr?.importe_total || 0), 0),
      descuento_de_gasto_de_ejecucion_id: descuento_de_gasto_de_ejecucion.map((d) => d.id),
      importe_del_descuento_de_gasto_de_ejecucion: descuento_de_gasto_de_ejecucion
        .reduce((acc, curr) => acc + (curr?.importe_total || 0), 0),
      // Recargos multas
      descuento_de_recargo_multa_id: descuento_de_recargo_multa.map((d) => d.id),
      importe_del_descuento_del_recargo_multa: descuento_de_recargo_multa
        .reduce((acc, curr) => acc + (curr?.importe_total || 0), 0),
      // Gastos multas
      descuento_de_gasto_multa_id: descuento_de_gasto_multa.map((d) => d.id),
      importe_del_descuento_de_gasto_multa: descuento_de_gasto_multa
        .reduce((acc, curr) => acc + (curr?.importe_total || 0), 0),
    };
  };

  const aggregateDiscounts = (preDiscounts) => {
    const output = { ...outputDiscountsStructure };

    Object.entries(output).forEach(([key, value]) => {
      output[key] = typeof value === 'number'
        ? preDiscounts.reduce((acc, curr) => acc + (curr[key] || 0), 0)
        : preDiscounts.reduce((acc, curr) => [...acc, ...(curr[key] || [])], []);
    });

    return output;
  };

  const getDiscounts = () => {
    const c = groupedCargos.conveniables;
    if (!c.length) return outputDiscountsStructure;

    const preDiscounts = c.map(processCargoDiscounts);

    return aggregateDiscounts(preDiscounts);
  };

  // region Create convenio
  const crearConvenio = async (nextStep) => {
    let _convenioId = null;
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const contentType = getContenTypePorTipoDePadron(tipoDePadron);
      values.content_type_padron = contentType;
      values.object_id_padron = padron.id;
      values.financiamiento = 0;
      values.padron_conveniado = tipoDePadron;
      values.cajero = user.id;
      values.adeudo = values.total;
      values.pagado = 0;
      values.ciudadano = ciudadano?.id;
      values.tiene_responsable_solidario = !!values.padron_ciudadano_responsable_solidario;
      values.unidad_responsable_id = user.unidad_responsable?.id || null;
      values.tiene_responsable_solidario = hasResponsable;
      if (!hasResponsable) {
        values.nombre_del_ciudadano_responsable = null;
        values.nombre_del_contribuyente_responsable = null;
      }
      values.tiene_afianzadora = hasAfianzadora;
      if (!hasAfianzadora) {
        values.importe_de_la_fianza = null;
        values.numero_de_fianza = null;
        values.nombre_de_la_afianzadora = null;
        values.linea_de_validacion_de_fianza = null;
      }
      if (tipoSelected?.configuracion_de_vencimiento_parcialidades === tiposDeVencimientoEnum
        .CUSTOM) {
        values.fecha_de_vencimiento = values.fecha_de_vencimiento.format('YYYY-MM-DD[T]HH:mm:ss');
      } else {
        values.fecha_de_vencimiento = null;
      }

      values.nombre_del_ciudadano = tipoDePadron === padronesEnum.CIUDADANO
        ? padronToString(padron, tipoDePadron) : '';
      values.nombre_del_contribuyente = tipoDePadron === padronesEnum.CONTRIBUYENTE
        ? padronToString(padron, tipoDePadron) : '';
      values.fecha_de_solicitud = values.fecha_de_solicitud.format(DATE_FORMAT);
      values.accionInf = 'CREAR_CONVENIO';

      const _convenio = await postConvenio(values);
      if (_convenio) {
        if (values?.file) {
          await postUploadFile(_convenio.id, values.file);
        }
        const valuesConvenio = {
          cargos: groupedCargos.conveniables.map((cargo) => cargo.id),
          tipo_de_padron: tipoDePadron,
          padron: padron.id,
          convenio: _convenio.id,
          ...getDiscounts(),
        };

        const _cargosConvenidos = await postCargosConvenidos(valuesConvenio);
        if (_cargosConvenidos) {
          const _parcialidades = await generarParcialidades({
            convenio: _convenio.id,
            fecha_de_vencimiento: values.fecha_de_vencimiento,
            accionInf: 'CARGOS_CONVENIOS',
          });
          if (_parcialidades) {
            _convenioId = _convenio.id;
            convenio.current = _convenio.id;
            personalAutorizado.current = values.personal_de_convenio;
            setCurrentStep(nextStep);
            setHasResponsable(false);
            setHasAfianzadora(false);
          }
        }
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
    return _convenioId;
  };

  const onCancel = () => {
    setVisible(false);
    ciudadanoName.current = '';
    contribuyenteName.current = '';
    setCurrentStep(stepsEnum.CONFIRMACION);
    setGroupedCargos({ conveniables: [], noConveniables: [] });
    form.resetFields();
    convenio.current = null;
    personalAutorizado.current = null;
    setTipoSelected(null);
    setHasResponsable(false);
    setHasAfianzadora(false);
    setTipoSelected(null);
    setBase64('');
    setVisiblePreview(false);
  };

  const totalCargosConveniables = useMemo(() => round(
    groupedCargos.conveniables.reduce((accum, curr) => accum + curr.adeudo_total, 0),
  ), [groupedCargos.conveniables]);

  // #region Fetching
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setLoading(true);
      const [_tipoDeConvenios, _personalDeConvenios] = await Promise.all([
        getTiposDeConvenios({ padron: tipoDePadron }),
        getPersonalDeConvenios({ estado_activo: true }),
      ]);

      const recargosIds = cargos
        .filter((c) => c.recargos?.length)
        .map((c) => c.recargos.map((r) => r.id)).flat();

      const gastosIds = cargos
        .filter((c) => c.gastos?.length)
        .map((c) => c.gastos.map((g) => g.id)).flat();

      const actualizacionesIds = cargos
        .filter((c) => c.actualizaciones?.length)
        .map((c) => c.actualizaciones.map((a) => a.id)).flat();

      const descuento_base = [
        ...cargos.map((c) => c.descuentos_aplicables),
        ...cargos.map((c) => c.descuentos_especiales),
      ].flat().filter((d) => d.aplicado).map((d) => d.id);

      const descuento_de_actualizacion_id = [
        ...cargos.map((c) => c.actualizaciones),
      ].flat().map((a) => a.descuentos).flat().filter((a) => a.aplicado)
        .map((a) => a.id);

      const descuento_de_gasto_de_ejecucion_id = [
        ...cargos.map((c) => c.gastos),
      ].flat().map((g) => g.descuentos).flat().filter((g) => g.aplicado)
        .map((g) => g.id);

      const descuento_de_recargo_id = [
        ...cargos.map((c) => c.recargos),
      ].flat().map((r) => r.descuentos).flat().filter((r) => r.aplicado)
        .map((r) => r.id);

      const recargos_multas = [
        ...cargos.map((c) => c.recargosMulta),
      ].flat().map((r) => r.id);

      const descuentos_de_recargos_multas = [
        ...cargos.map((c) => c.recargosMulta),
      ].flat().map((r) => r.descuentos).flat().filter((r) => r.aplicado)
        .map((r) => r.id);

      const gastos_multas = [
        ...cargos.map((c) => c.gastosMulta),
      ].flat().map((g) => g.id);

      const descuentos_gastos_multa = [
        ...cargos.map((c) => c.gastosMulta),
      ].flat().map((g) => g.descuentos).flat().filter((g) => g.aplicado)
        .map((g) => g.id);

      const response = await postVerificarImportesConvenio({
        cargos: selectedRowKeys,
        recargos: recargosIds,
        gastos: gastosIds,
        actualizaciones: actualizacionesIds,
        total_a_convenir: round(cargos.reduce((acum, curr) => (acum + curr.adeudo_total), 0)),
        descuento_base_id: descuento_base,
        descuento_de_actualizacion_id,
        descuento_de_gasto_de_ejecucion_id,
        descuento_de_recargo_id,
        descuentos_de_recargos_multas,
        gastos_multas,
        descuentos_gastos_multa,
        recargos_multas,
      });
      if (response.necesita_recalculo) {
        message.info('Es necesario volver a consultar el padrón');
        onCancel();
        resetCaja();
        return;
      }

      const { conveniables, noConveniables } = groupCargos(cargos);

      if (mounted) {
        setCurrentStep(stepsEnum.CONFIRMACION);
        form.resetFields();
        convenio.current = null;
        setTipoDeConvenios(_tipoDeConvenios.filter((tc) => !!tc.reportes_de_convenio_id));
        setPersonalDeConvenios(_personalDeConvenios);
        setGroupedCargos({ conveniables, noConveniables });
      }
      setLoading(false);
    };
    if (visible && padron && (selectedRowKeys?.length || cargos?.length)) {
      fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [visible, selectedRowKeys.length]);

  const onChangeParcialidades = () => {
    const {
      numero_de_pagos,
      anticipo: _anticipo,
      total: _total,
    } = form.getFieldsValue();
    if (numero_de_pagos && _anticipo && _total) {
      const total = typeof _total === 'string' ? parseFloat(_total) : _total;
      const anticipo = typeof _anticipo === 'string' ? parseFloat(_anticipo) : _anticipo;
      const importe_por_parcialidad = round(((total - anticipo) / numero_de_pagos));
      form.setFieldsValue({ importe_por_parcialidad });
    }
  };

  const onChangeAnticipoPorcentaje = () => {
    const { porcentaje, total } = form.getFieldsValue();
    if (total && porcentaje) {
      const anticipo = round((((porcentaje * total) / 100).toFixed(2) * 100) / 100);
      if (anticipo >= total) {
        message.warn('No se puede dar un anticipo mayor o igual al total a convenir');
        form.resetFields(['anticipo']);
      } else {
        form.setFieldsValue({ anticipo });
        onChangeParcialidades();
      }
    }
  };

  const onChangeAnticipoImporte = async () => {
    const { anticipo, total } = form.getFieldsValue();
    if (total && anticipo) {
      const porcentaje = (((anticipo * 100) / total).toFixed(2) * 100) / 100;
      if (porcentaje >= 100) {
        message.warn('No se puede dar un anticipo mayor o igual al 100%');
        form.resetFields(['porcentaje']);
      } else {
        form.setFieldsValue({ porcentaje });
        onChangeParcialidades();
      }
    }
  };

  const rules = {
    required: [{ required: true }],
    parcialidades: [{
      validator: async () => {
        const { tipo_de_convenio, numero_de_pagos } = form.getFieldsValue();
        if (tipo_de_convenio && numero_de_pagos) {
          const { cantidad_maxima_de_parcialidades } = tipoDeConvenios
            .find((tc) => tc.id === tipo_de_convenio);
          if (cantidad_maxima_de_parcialidades !== 0
            && numero_de_pagos > cantidad_maxima_de_parcialidades) {
            form.resetFields(['importe_por_parcialidad']);
            throw new Error('Se esta excediendo el número máximo de parcialidades.');
          }
        }
      },
    }],
    porcentaje: [{
      validator: async () => {
        const { porcentaje } = form.getFieldsValue();
        if (porcentaje >= 100) {
          throw new Error('No se puede dar un anticipo mayor o igual al 100%');
        }
      },
    }],
    anticipo: [{
      validator: async () => {
        const { total, anticipo } = form.getFieldsValue();
        if (anticipo >= total) {
          throw new Error('No se puede dar un anticipo mayor o igual al total a convenir');
        }
      },
    }],
  };

  const onChangeTipoDeConvenio = (val) => {
    if (!val) {
      form.resetFields(['requisitos_del_convenio', 'numero_de_pagos']);
    }
    const match = tipoDeConvenios.find((e) => e.id === val);
    if (match) {
      form.setFieldsValue({
        requisitos_del_convenio: match.requisitos_del_convenio,
      });
      setTipoSelected(match);
    }
  };

  const prev = () => {
    const prevStep = currentStep - 1;
    if (prevStep === stepsEnum.CONFIRMACION) {
      form.resetFields();
    }
    if (currentStep === stepsEnum.CONFIRMACION) {
      onCancel();
    } else {
      setCurrentStep(prevStep);
    }
  };

  const next = async () => {
    if (!hasPermission) {
      message.warn('No tiene los permisos requeridos para continuar.');
      return;
    }
    const nextStep = currentStep + 1;
    if (nextStep === stepsEnum.PARCIALIDADES) {
      setCurrentStep(nextStep);
      const total = round((groupedCargos.conveniables
        .reduce((accum, curr) => accum + curr.adeudo_total, 0)));
      form.setFieldsValue({ total });
    }
    if (nextStep === stepsEnum.COBRO) {
      await crearConvenio(nextStep);
    }
    if (currentStep === stepsEnum.COBRO) {
      dispatch({
        type: SET_PADRON_REQUIERE_FETCH,
        payload: padron.key,
      });

      message.success('Convenio creado con éxito, de ser necesario vuelva a consultar el padrón.');

      setVisible(false);
      setCurrentStep(stepsEnum.CONFIRMACION);
    }
  };

  const paginationSettingsList = {
    pageSize: 5,
    hideOnSinglePage: true,
    size: 'small',
  };

  const SectionConfirm = () => (
    <>
      {!tipoDeConvenios?.length && (
        <Row style={{ alignItems: 'baseline' }}>
          <Text className="danger">
            <span>
              No hay tipos de convenio disponibles para este padrón
            </span>
            {' '}
            <Tooltip
              title="Los tipos de convenios que les hace falta configuración no se toman como tipos validos"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </Text>
          <Button
            type="link"
            onClick={() => {
              setVisible(false);
              history.push('/convenios/tipos-de-convenios');
            }}
          >
            Crear un tipo de convenio
            <ArrowRightOutlined />
          </Button>
        </Row>
      )}
      {isDev && (
        <span>
          {`(${groupedCargos.conveniables.length}/${selectedRowKeys?.length})`}
        </span>
      )}
      {!!groupedCargos.conveniables.length && (
        <Row>
          <Col span={24}>
            Monto total a convenir:
            {' '}
            <Text strong>
              {currencyFormatter(totalCargosConveniables)}
            </Text>
          </Col>
          <Col span={24}>
            <List
              header={<Title level={4}>Cargos a convenir</Title>}
              dataSource={groupedCargos.conveniables}
              size="small"
              pagination={paginationSettingsList}
              renderItem={(item) => (
                <List.Item>
                  <Text type="success">
                    {`${item?.tipo_de_cargo?.periodo_fiscal?.periodo} - ${item.descripcion} - ${currencyFormatter(item.adeudo_total)}` }
                  </Text>
                </List.Item>
              )}
            />
          </Col>
        </Row>
      )}
      {!!groupedCargos.noConveniables.length && (
      <List
        header={<Title level={4}>Cargos no disponibles para convenir</Title>}
        dataSource={groupedCargos.noConveniables}
        size="small"
        pagination={paginationSettingsList}
        renderItem={(item) => (
          <List.Item>
            <Text type="danger">
              {`${item.descripcion} - ${currencyFormatter(item.adeudo_total)}` }
            </Text>
          </List.Item>
        )}
      />
      )}
    </>
  );

  const FormParcialidades = () => (
    <Form
      layout="vertical"
      form={form}
      scrollToFirstError
      onFinish={next}
    >
      <Row gutter={[10]}>
        <Col span={8}>
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item
                name="requisitos_del_convenio"
                hasFeedback
                label="Requisitos"
              >
                <Input.TextArea
                  autoSize={{ minRows: 5, maxRows: 6 }}
                  maxLength={250}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="observaciones"
                hasFeedback
                label="Observaciones"
                normalize={capitalize}
              >
                <Input.TextArea
                  autoSize={{ minRows: 5, maxRows: 6 }}
                  maxLength={250}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col span={16}>
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Form.Item
                name="fecha_de_solicitud"
                rules={rules.required}
                label="Fecha de solicitud"
              >
                <DatePicker
                  disabledDate={(date) => date
                    .isBefore(new Moment()
                      .subtract(4, 'month')
                      .subtract(1, 'day'))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6}>
              <NumericInput
                prefix="$"
                name="total"
                label="Importe total a convenir"
                required
                disabled
                hasFeedback
                decimal
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="descripcion"
                label="Descripción"
                normalize={capitalize}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={6}>
              <Form.Item
                name="personal_de_convenio"
                rules={rules.required}
                label="Personal de firma"
              >
                <Select dataSource={personalDeConvenios} labelProp="nombre" />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left" style={{ margin: 5 }}>
            <Title level={5}>Anticipo</Title>
          </Divider>
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="tipo_de_convenio"
                rules={rules.required}
                label="Tipo de Convenio"
              >
                <Select
                  onChange={onChangeTipoDeConvenio}
                  dataSource={tipoDeConvenios}
                  onClear={() => {
                    setTipoSelected(null);
                    form.resetFields(['requisitos_del_convenio', 'numero_de_pagos']);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <NumericInput
                suffix="%"
                name="porcentaje"
                label="Porcentaje"
                onChange={onChangeAnticipoPorcentaje}
                decimal
                rules={rules.porcentaje}
                required
                hasFeedback
              />
            </Col>
            <Col xs={24} sm={24} md={8}>
              <NumericInput
                prefix="$"
                rules={rules.anticipo}
                name="anticipo"
                label="Importe de anticipo"
                onChange={onChangeAnticipoImporte}
                required
                hasFeedback
                decimal
                className="input-importe"
              />
            </Col>
            {tipoSelected?.configuracion_de_vencimiento_parcialidades === tiposDeVencimientoEnum
              .CUSTOM
            && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="fecha_de_vencimiento"
                  rules={rules.required}
                  label="Día de vencimiento"
                >
                  <DatePicker disabledDate={(date) => date.isBefore(new Moment().subtract(1, 'day'))} />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Divider orientation="left" style={{ margin: 5 }}>
            <Title level={5}>Parcialidades</Title>
          </Divider>
          <Row gutter={[10]}>
            <Col xs={24} sm={24} md={12}>
              <NumericInput
                name="numero_de_pagos"
                label="Número de parcialidades"
                onChange={onChangeParcialidades}
                required
                rules={rules.parcialidades}
                hasFeedback
                maxLength={`${tipoSelected?.cantidad_maxima_de_parcialidades}`.length}
                placeholder={tipoSelected
                  && `Máximo ${tipoSelected?.cantidad_maxima_de_parcialidades || 0}`}
              />
            </Col>
            <Col xs={24} sm={24} md={12}>
              <NumericInput
                prefix="$"
                name="importe_por_parcialidad"
                label="Importe por parcialidad"
                disabled
                required
                hasFeedback
                decimal
                className="input-importe"
              />
            </Col>
          </Row>
        </Col>
        <Form.Item hidden>
          <Button htmlType="submit" />
        </Form.Item>
      </Row>
    </Form>
  );

  const generarDocumentoDeConvenio = async () => {
    setLoading(true);
    const values = {
      ID: tipoSelected?.reportes_de_convenio_id,
      Parametros: [
        {
          Nombre: 'convenio',
          TipoParametro: 'Entero',
          Valor: convenio.current,
        },
        {
          Nombre: 'personal_autorizado',
          TipoParametro: 'Entero',
          Valor: personalAutorizado.current,
        },
      ],
    };
    const _base64 = await postObtenerReporte(values);
    if (_base64) {
      setBase64(_base64);
      setVisiblePreview(true);
    } else {
      message.warn('No se pudo obtener el documento del convenio');
    }
    setLoading(false);
  };

  // #region Steps
  const steps = {
    [stepsEnum.CONFIRMACION]: {
      title: (
        <Tooltip
          placement="bottom"
          title="Solo los cargos en verde son los que estan disponibles para convenio."
        >
          <span>Confirmación</span>
        </Tooltip>
      ),
      content: <SectionConfirm />,
    },
    [stepsEnum.PARCIALIDADES]: {
      title: (
        <Tooltip
          placement="bottom"
          title="Configura como se van a pagar las parcialidades del convenio."
        >
          <span>Creación de Parcialidades</span>
        </Tooltip>
      ),
      content: <FormParcialidades />,
    },
    [stepsEnum.COBRO]: {
      title: (
        <Tooltip
          placement="bottom"
          title="Genera el documento del convenio y la tabla de amortización."
        >
          <span>Parcialidades</span>
        </Tooltip>
      ),
      content: !visiblePreview ? (
        <SectionCobros
          setLoading={setLoading}
          convenio={convenio.current}
          callbackReporte={generarDocumentoDeConvenio}
        />
      ) : (
        <div style={{ height: '70vh' }}>
          <Previewer
            base64={base64}
            visible
            setVisible={setVisiblePreview}
            modal={false}
          />
        </div>
      ),
    },
  };

  const disableNext = currentStep === stepsEnum.CONFIRMACION
  && (!groupedCargos.conveniables.length || !tipoDeConvenios.length);

  return (
    <Modal
      title={(
        <Row justify="space-between">
          <Text>Crear convenio</Text>
        </Row>
      )}
      open={visible}
      footer={false}
      closable={false}
      width="90%"
      centered
      forceRender
    >
      <Spin spinning={loading} tip="Cargando">
        <Row justify="space-between">
          <Col>
            <Button
              disabled={currentStep === stepsEnum.COBRO}
              type="link"
              onClick={() => prev()}
            >
              <ArrowLeftOutlined />
              {currentStep === stepsEnum.CONFIRMACION ? 'Cancelar' : 'Anterior'}
            </Button>
          </Col>
          <Col>
            <Steps progressDot size="small" current={currentStep}>
              {Object.values(steps).map((item) => (
                <Steps.Step key={item.title} title={item.title} description={item.description} />
              ))}
            </Steps>
          </Col>
          <Col>
            <Button
              type="link"
              onClick={() => next()}
              disabled={disableNext}
            >
              {currentStep === stepsEnum.COBRO ? 'Finalizar' : 'Siguiente'}
              <ArrowRightOutlined />
            </Button>
          </Col>
        </Row>
        <StepsContent>
          {steps[currentStep].content}
        </StepsContent>
      </Spin>
    </Modal>
  );
};

const StepsContent = styled.div`
  height: ${({ height }) => height || 'auto'};
  background-color: #fafafa;
  border: 1px dashed #e9e9e9;
  border-radius: 2px;
  overflow: hidden scroll;
  padding: 20px;
  /* min-height: 565px; */
`;

ModalConvenio.propTypes = {
  visible: PropTypes.bool,
  setVisible: PropTypes.func.isRequired,
  tipoDePadron: PropTypes.number,
  padron: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.number,
    tipo_de_padron: PropTypes.number,
    cargos: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      convenios: PropTypes.shape({
        id: PropTypes.number,
      }),
    })),
    tramite: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  cargos: PropTypes.arrayOf(PropTypes.number),
  hasPermission: PropTypes.bool.isRequired,
  resetCaja: PropTypes.func.isRequired,
};

ModalConvenio.defaultProps = {
  visible: false,
  padron: null,
  tipoDePadron: 0,
  cargos: [],
};

export default ModalConvenio;
